import deepEqual from "fast-deep-equal";
import type { Dispatch, SetStateAction } from "react";
import { use, useCallback, useMemo, useState } from "react";
import { createApiResponse } from "@entrilia/shared/api/axiosHelper";
import useFetch from "@entrilia/shared/hooks/useFetch";
import type {
  FieldIdToValueMap,
  ObjectCardPreview,
  ObjectCardPreviewRequest,
  RelatedObjectsResponse,
} from "../../api/types/objectTypes";
import ObjectCardContext from "./ObjectCardContext";
import {
  acknowledgeDefaultsAction,
  applyRowDefaultsAction,
  getCellDecorations,
  getAutoFillsOverriddenByEdit,
  getFieldDefaults,
  getFieldsAutoFillTooltips,
  getObjectCardPreviewRequest,
  giveUpDefaultsAction,
  initialObjectCardPreviewSession,
  rememberAutoFillsAction,
  requestsDefaults,
} from "./objectCardPreviewState";
import type { FieldValueLabeler } from "./objectCardPreviewState";
import type { RelatedObjectValuesState } from "./relatedObjectValuesState";

interface Params {
  objectId: string;
  storedFieldValues: FieldIdToValueMap;
  fieldValues: FieldIdToValueMap;
  storedRelatedObjects: RelatedObjectsResponse[] | undefined;
  relatedObjectValuesState: RelatedObjectValuesState;
  setRelatedObjectValuesState: Dispatch<SetStateAction<RelatedObjectValuesState>>;
  onFieldDefaults: (fieldDefaults: FieldIdToValueMap) => void;
  onRowDefaults: (fieldIds: string[]) => void;
}

const useObjectCardPreview = ({
  objectId,
  storedFieldValues,
  fieldValues,
  storedRelatedObjects,
  relatedObjectValuesState,
  setRelatedObjectValuesState,
  onFieldDefaults,
  onRowDefaults,
}: Params) => {
  const cardContext = use(ObjectCardContext);
  const config = cardContext?.cardPreview;
  const setPreviewChecks = cardContext?.setPreviewChecks;

  const [session, setSession] = useState(initialObjectCardPreviewSession);
  const [sentRequest, setSentRequest] = useState<ObjectCardPreviewRequest>();

  const storedRows = storedRelatedObjects?.find((x) => x.objectType === config?.relatedObjectType)?.relatedObjects;

  const request =
    config && storedRows
      ? getObjectCardPreviewRequest(
          config,
          session,
          storedFieldValues,
          fieldValues,
          storedRows,
          relatedObjectValuesState
        )
      : undefined;

  if (!deepEqual(request, sentRequest)) {
    setSentRequest(request);
  }

  const getPreview = useCallback(
    async (signal?: AbortSignal) => {
      if (!config || !sentRequest) {
        return createApiResponse<ObjectCardPreview>({ fieldValues: {}, defaults: [], autoFilled: [], checks: [] });
      }

      const handlePreviewFailed = () => {
        if (!signal?.aborted) {
          setSession(giveUpDefaultsAction(config, sentRequest));
          setPreviewChecks?.("unavailable");
        }
      };

      try {
        const response = await config.getPreview(objectId, sentRequest);
        if (!response.success) {
          handlePreviewFailed();
        }
        return response;
      } catch (error) {
        handlePreviewFailed();
        throw error;
      }
    },
    [config, objectId, sentRequest, setPreviewChecks]
  );

  const handlePreviewLoaded = (loadedPreview: ObjectCardPreview) => {
    if (!config || !sentRequest || !storedRows) {
      return;
    }

    setSession(acknowledgeDefaultsAction(config, sentRequest, loadedPreview));
    setSession(rememberAutoFillsAction(loadedPreview));
    setPreviewChecks?.(loadedPreview.checks);

    const fieldDefaults = getFieldDefaults(sentRequest, loadedPreview, fieldValues);
    if (Object.keys(fieldDefaults).length > 0) {
      onFieldDefaults(fieldDefaults);
    }

    setRelatedObjectValuesState(applyRowDefaultsAction(config, sentRequest, loadedPreview, storedRows));
    onRowDefaults(
      loadedPreview.defaults.filter((d) => d.relatedObjectKey !== undefined && d.value).map((d) => d.fieldId)
    );
  };

  const [lastPreview, previewError] = useFetch(getPreview, {
    disabled: sentRequest === undefined,
    onSetData: handlePreviewLoaded,
  });
  const preview = previewError ? undefined : lastPreview;

  const fieldsAutoFillTooltips = useMemo(
    () => (config && preview ? getFieldsAutoFillTooltips(config, preview, fieldValues) : undefined),
    [config, preview, fieldValues]
  );

  const cellDecorations = useMemo(
    () => (config && preview ? getCellDecorations(config, preview, relatedObjectValuesState) : undefined),
    [config, preview, relatedObjectValuesState]
  );

  const relatedNoOptionsTexts = useMemo(() => config?.getRelatedNoOptionsTexts?.(fieldValues), [config, fieldValues]);

  const reset = useCallback(() => setSession(initialObjectCardPreviewSession), []);

  const reportUserChange = useCallback(
    (
      relatedObjectKey: string | undefined,
      oldValues: FieldIdToValueMap,
      newValues: FieldIdToValueMap,
      labelOf: FieldValueLabeler
    ) => {
      const onAutoFillOverridden = config?.onAutoFillOverridden;
      if (!onAutoFillOverridden || !preview) {
        return;
      }

      getAutoFillsOverriddenByEdit(preview.autoFilled, relatedObjectKey, oldValues, newValues).forEach((fill) =>
        onAutoFillOverridden(objectId, fill, {
          from: labelOf(fill.fieldId, fill.value),
          to: labelOf(fill.fieldId, newValues[fill.fieldId]),
        })
      );
    },
    [config, objectId, preview]
  );

  return {
    config,
    fieldValues: preview?.fieldValues,
    hasFailed: previewError !== undefined || session.defaultsMissed === true,
    isWaitingForDefaults: !previewError && request !== undefined && requestsDefaults(request),
    fieldsAutoFillTooltips,
    cellDecorations,
    relatedNoOptionsTexts,
    reset,
    reportUserChange,
  };
};

export default useObjectCardPreview;
